// src/stores/auth.ts
import {defineStore} from 'pinia'
import router from '@/router/index';
import {storage} from '@/utils/reactiveStorage';

interface AuthState {
    accessToken: string | null;
    idToken: string | null;
    expiresIn: number | null;
    tokenType: string | null;
    isAuthenticated: boolean;
    email: string | null;
}

interface UserInformation {
    email: string;

    [key: string]: any;
}

/**
 * Extracts user information from the ID token.
 * @param {string | null} idToken - The ID token to parse.
 * @returns {UserInformation | null} The parsed user information or null if parsing fails.
 */
function parseJwt(idToken: string | null): UserInformation | null {
    if (!idToken) return null;

    const tokenParts = idToken.split('.');
    if (tokenParts.length < 2) return null;

    try {
        const base64Payload = tokenParts[1]
            .replace(/_/g, '/')
            .replace(/-/g, '+');

        const base64 = base64Payload.padEnd(
            base64Payload.length + (4 - (base64Payload.length % 4)) % 4,
            '='
        );

        const rawPayload = atob(base64);
        const payload = JSON.parse(rawPayload);

        if (!payload || typeof payload !== 'object' || !('email' in payload)) {
            return null;
        }

        return payload as UserInformation;
    } catch (error) {
        return null;
    }
}

/**
 * Checks if the user is authenticated based on the expiration date.
 * @returns {boolean} True if the session is valid, false otherwise.
 */
function isSessionValid(): boolean {
    const expiresAt = storage.get('expiresAt', 0);
    return !!storage.get('accessToken') && expiresAt !== null && expiresAt > Date.now();
}

export const useAuthStore = defineStore('auth', {
    /**
     * The state of the auth store.
     * @returns {AuthState} The initial state.
     */
    state: (): AuthState => ({
        accessToken: storage.get('accessToken', null),
        idToken: storage.get('idToken', null),
        expiresIn: storage.get('expiresIn', null),
        tokenType: storage.get('tokenType', null),
        email: storage.get('email', null),
        isAuthenticated: isSessionValid()
    }),

    getters: {
        /**
         * Gets the access token.
         * @param {AuthState} state - The state of the auth store.
         * @returns {string | null} The access token.
         */
        getAccessToken: (state) => state.accessToken,

        /**
         * Checks if the user is logged in.
         * @param {AuthState} state - The state of the auth store.
         * @returns {boolean} True if the user is logged in, false otherwise.
         */
        isLoggedIn: (state) => state.isAuthenticated,
    },

    actions: {
        /**
         * Initializes the authentication state.
         */
        initializeAuth() {
            if (isSessionValid()) {
                this.accessToken = storage.get('accessToken', null);
                this.idToken = storage.get('idToken', null);
                this.expiresIn = storage.get('expiresIn', null);
                this.tokenType = storage.get('tokenType', null);
                this.email = storage.get('email', null);
                this.isAuthenticated = true;
            } else {
                this.logout();
            }
        },

        /**
         * Redirects the user to the login page.
         */
        redirectToLogin() {
            const authParams = new URLSearchParams({
                client_id: process.env.VUE_APP_AUTH_CLIENT_ID,
                redirect_uri: process.env.VUE_APP_AUTH_REDIRECT_URI,
                response_type: 'token',
                scope: 'openid email',
            });

            window.location.href = `${process.env.VUE_APP_AUTH_URL}?${authParams.toString()}`;
        },

        /**
         * Logs out the user and clears the authentication state.
         */
        logout() {
            // Reset state
            this.accessToken = null;
            this.idToken = null;
            this.expiresIn = null;
            this.tokenType = null;
            this.isAuthenticated = false;
            this.email = null;

            // Clear storage
            const keysToRemove = ['accessToken', 'idToken', 'expiresIn', 'tokenType', 'expiresAt', 'email'];
            keysToRemove.forEach(key => storage.remove(key));
        },

        /**
         * Handles the authentication callback and updates the state.
         * @returns {Promise<boolean>} True if authentication is successful, false otherwise.
         */
        async handleAuthCallback() {
            try {
                const url = new URL(window.location.href);
                const searchParams = new URLSearchParams(url.hash.substring(1));

                // Extract auth data
                const accessToken = searchParams.get('access_token');
                const idToken = searchParams.get('id_token');
                const expiresIn = parseInt(searchParams.get('expires_in') || '0', 10);
                const tokenType = searchParams.get('token_type');

                // Validate required parameters
                if (!accessToken || !expiresIn || !tokenType) {
                    throw new Error('Missing required authentication parameters in URL');
                }

                // Parse user info from JWT
                const userInfo = parseJwt(idToken);
                if (!userInfo) {
                    throw new Error('Unable to extract user information from token');
                }

                // Update state
                this.accessToken = accessToken;
                this.idToken = idToken;
                this.expiresIn = expiresIn;
                this.tokenType = tokenType;
                this.isAuthenticated = true;
                this.email = userInfo.email;

                // Calculate expiration
                const expiresAt = Date.now() + (expiresIn * 1000);

                // Persist to storage
                storage.set('accessToken', accessToken);
                storage.set('idToken', idToken);
                storage.set('expiresIn', expiresIn);
                storage.set('tokenType', tokenType);
                storage.set('expiresAt', expiresAt);
                storage.set('email', userInfo.email);

                // Redirect to dashboard
                router.push('/dashboard');
                return true;
            } catch (error) {
                console.error('Error during authentication:', error);
                this.logout();
                return false;
            }
        }
    }
});

// Export for external use
export const getUserInformation = parseJwt;